import {
  type KeyboardEventHandler,
  useCallback,
  useEffect,
  useState,
} from 'react';
import {useHistory} from 'react-router-dom';

import {useSearchNavAnalytics} from '../Search/useSearchNavAnalytics';

type AppSearchInnerKeyboardControlsParams = {
  searchTerm: string;
  currentResultCount: number;
};

type AppSearchInnerKeyboardControls = {
  onKeyDown: KeyboardEventHandler;
};

export function useAppSearchInnerKeyboardControls({
  searchTerm,
  currentResultCount,
}: AppSearchInnerKeyboardControlsParams): AppSearchInnerKeyboardControls {
  const history = useHistory();
  const {trackAppLauncherUsed, trackAppLauncherFailure} =
    useSearchNavAnalytics();
  const [selection, setSelection] = useState(0);

  const onKeyDown: KeyboardEventHandler = useCallback(
    e => {
      if (e.key === 'Enter') {
        const links = document.querySelectorAll<HTMLAnchorElement>(
          '#launcher-option-trap > a'
        );
        if (links.length > 0) {
          const details = links[selection];
          // we don't need to track app launcher use here since the `onclick` handler on each
          // child link will track it
          details.click();
        } else if (searchTerm.startsWith('/')) {
          trackAppLauncherUsed(searchTerm, 'direct-navigation');

          history.push(encodeURI(searchTerm.trim()));
        } else {
          trackAppLauncherFailure(searchTerm);
        }
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (selection < currentResultCount - 1) {
          setSelection(selection + 1);
        }
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (selection > 0) {
          setSelection(selection - 1);
        }
      }
    },
    [
      selection,
      searchTerm,
      history,
      currentResultCount,
      trackAppLauncherUsed,
      trackAppLauncherFailure,
    ]
  );

  // update the selection value if the num of results changes outside the selection
  useEffect(() => {
    if (currentResultCount === 0) {
      setSelection(0);
    } else if (currentResultCount > 0 && selection > currentResultCount - 1) {
      setSelection(currentResultCount - 1);
    }
  }, [currentResultCount, selection]);

  return {onKeyDown};
}
